import React, { useContext } from 'react';
import { Badge, Box, Collapse, Divider, IconButton } from '@mui/material';
import { ReactElement } from 'react';
import { UserContext } from '../context/user-context';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import {
  CAPTURE_PATRON_DETAILS,
  LOGIN_ROUTE,
  ORDER_TRACK_ROUTE,
  PAYMENT_ROUTE,
  PROFILE_ROUTE,
  RSA_LIMIT,
  VERIFY_AGE,
  VIEW_ALL_ORDERS,
  VIEW_CART_ROUTE,
} from '../constants/routes';
import PastOrdersBanner from './past-orders-banner';
import { OrderContext } from '../context/order-context';
import { ReactComponent as ProfileIcon } from '../assets/icons/ICON_PROFILE.svg';
import { ReactComponent as CartIcon } from '../assets/icons/ICON_CART.svg';
import { ReactComponent as BackIcon } from '../assets/icons/ICON_BACK.svg';
import { ReactComponent as ProfileFilledIcon } from '../assets/icons/ICON_PROFILE_FILLED.svg';
import { CartContext } from '../context/cart-context';

const routesToExcludeBanner = [{ path: VIEW_ALL_ORDERS }];

interface Props {
  leftComponent?: ReactElement;
  middleComponent?: ReactElement;
  middleComponentExpanded?: ReactElement;
  rightComponent?: ReactElement;
  disableProfileIcon?: boolean;
  backIconRoute?: string;
  onBackClick?: () => void;
  isExpanded?: boolean;
  showCart?: boolean;
}

export function Header({
  leftComponent,
  middleComponent,
  middleComponentExpanded,
  rightComponent,
  disableProfileIcon,
  backIconRoute,
  onBackClick,
  isExpanded,
  showCart = true,
}: Props) {
  const userContext = useContext(UserContext);
  const orderContext = useContext(OrderContext);
  const { cartItemsCount } = useContext(CartContext);

  const navigate = useNavigate();
  const location = useLocation();

  const handleProfile = (event: React.MouseEvent<HTMLElement>) => {
    userContext.cognitoUser ? navigate(PROFILE_ROUTE) : navigate(LOGIN_ROUTE);
  };

  const handleCart = (event: React.MouseEvent<HTMLElement>) => {
    navigate(VIEW_CART_ROUTE);
  };

  const handleBack = (event: React.MouseEvent<HTMLElement>) => {
    if (onBackClick) {
      onBackClick();
    } else if (backIconRoute) {
      navigate(backIconRoute);
    } else {
      navigate(-1);
    }
  };

  const showActiveOrderBanner = () =>
    orderContext.hasActiveOrders &&
    !matchRoutes(routesToExcludeBanner, location);

  return (
    <Box>
      <Box
        display="flex"
        sx={{
          boxShadow: '0 4px 6px -6px grey',
          alignItems: 'flex-start', // Prevents auto-centering during expansion
          overflow: 'hidden', // Prevents items hidden in collapsed view from overflowing during transitions
        }}
        pt={2}
        pb={2}
      >
        {/* Left Section - Fixed to top */}
        <Box
          sx={{
            width: '20%',
            height: '6vh', // Fixed height to ensure vertical centering remains stable during expansion
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {leftComponent ? (
            <Box ml={1}>{leftComponent}</Box>
          ) : (
            <IconButton onClick={handleBack} sx={{ ml: 2 }}>
              <BackIcon />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'stretch',
            ml: 2,
          }}
        >
          <Divider
            orientation="vertical"
            flexItem={!isExpanded} // Fixes dividers working with expansion
            sx={{
              borderColor: '#2A2A2A',
              height: isExpanded ? '75%' : '100%',
              transition: 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </Box>

        {/* Middle Section - Expandable */}
        <Box
          sx={{
            width: '60%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start', // Ensures content that is always visible stays pinned to top
            textAlign: 'center',
            minHeight: '6vh',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '6vh',
            }}
          >
            {middleComponent}
          </Box>
          <Collapse
            in={isExpanded}
            timeout={{ enter: 300, exit: 400 }}
            easing={{
              enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
              exit: 'cubic-bezier(0.4, 0, 0.6, 1)',
            }}
          >
            <Box sx={{ pt: 1 }}>{middleComponentExpanded}</Box>
          </Collapse>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'stretch',
            mr: 2,
          }}
        >
          <Divider
            orientation="vertical"
            flexItem={!isExpanded} // Fixes dividers working with expansion
            sx={{
              borderColor: '#2A2A2A',
              height: isExpanded ? '75%' : '100%',
              transition: 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </Box>
        {/* Right Section - Fixed to top */}
        <Box
          sx={{
            width: '25%',
            height: '6vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            marginX: cartItemsCount ? 2 : 0,
          }}
        >
          {rightComponent ? (
            <Box ml={1}>{rightComponent}</Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
            >
              <Box marginRight="5px">
                {userContext.cognitoUser && !disableProfileIcon ? (
                  <IconButton onClick={handleProfile} style={{ padding: 0 }}>
                    <ProfileFilledIcon />
                  </IconButton>
                ) : (
                  <IconButton onClick={handleProfile} style={{ padding: 0 }}>
                    <ProfileIcon />
                  </IconButton>
                )}
              </Box>
              {showCart && (
                <Box>
                  <IconButton onClick={handleCart} sx={{ padding: 0, mr: 2 }}>
                    <Badge color="error" badgeContent={cartItemsCount}>
                      <CartIcon />
                    </Badge>
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      {showActiveOrderBanner() && <PastOrdersBanner />}
    </Box>
  );
}
